import { QuadtreeItem } from 'quadtree-lib';

import Game from '../game';
import { IdentifierType } from '../baseObject';
import Control, { ControlParams } from './control';
import Element from '../element/element';
import Ship from '../element/ship/ship';
import CapitalShip from '../element/ship/capitalShip';
import Menu from '../ui/menu/menu';
import ControlGroup, { ControlGroupParams } from './controlGroup';

import MoveToOrder from '../order/moveToOrder';
import StrafeToOrder from '../order/strafeToOrder';
import FollowOrder from '../order/followOrder';
import AttackOrder from '../order/attackOrder';
import AttackMoveToOrder from '../order/attackMoveToOrder';
import HoldPositionOrder from '../order/holdPositionOrder';
import PatrolOrder from '../order/patrolOrder';

import { Faction, getFactionTargetGroups } from '../../utils/faction';
import { isBetween } from '../../utils/math';
import { Iterator } from '../../utils/types';
import { CoordsType } from '../../utils/geometry';
import LevelScene from '../scene/LevelScene';


interface Selection {
  x: number,
  y: number,
  width: number,
  height: number,
  selecting: boolean,
  onMinimap: boolean,
}

interface Color {
  selector: string,
  hover: string,
  hoverNeutral: string,
  hoverHazard: string,
  hoverEnemy: string,
}

enum LeftClickMode {
  SELECT,
  ATTACK,
  PATROL,
}

export interface RTSControlsParams extends ControlParams { }

export default class RTSControls extends Control implements RTSControlsParams {
  static selectionWidth = 1;
  static colors: Color = {
    selector: 'darkgreen',
    hover: 'lightgreen',
    hoverNeutral: 'yellow',
    hoverHazard: 'orange',
    hoverEnemy: 'red',
  };

  selectedElementsIds: Array<IdentifierType> = [];
  hoveredElementsIds: Array<IdentifierType> = [];
  selection: Selection;
  currentLeftClickMode?: LeftClickMode;
  controlGroups: Iterator<ControlGroup>;

  rightMouseButtonPressed = false;
  ctrlKeyPressed = false;

  doubleSelectTimeout = 250; // ms
  lastSelectTimestamp = 0;

  constructor(params: RTSControlsParams) {
    super(params);
    this.selection = {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      selecting: false,
      onMinimap: false,
    };

    /* Create the control groups for the 0123456789 keys */
    this.controlGroups = {};
    for (let i = 0; i <= 9; i++) {
      this.controlGroups[i] = new ControlGroup({ scene: this.scene } as ControlGroupParams);
    }
  }

  get scene() {
    return Game.instance.scene as LevelScene;
  }

  draw() {
    this.drawSelector();
  }

  getPointerCoords(e: MouseEvent, ignoreCamera = false): { x: number, y: number } {
    const cursor = Game.instance.cursor;
    if (cursor && cursor.hasLock) {
      let { x = 0, y = 0 } = cursor;
      if (ignoreCamera) {
        const camera = this.getCamera();
        if (camera) {
          x -= camera.x - camera.width / 2;
          y -= camera.y - camera.height / 2;
        }
      }

      return { x, y };
    }
    else
      return super.getPointerCoords(e, ignoreCamera);
  }

  handleMovementOrder(x: number, y: number, e: MouseEvent, rightButtonStillDown = false): void {
    if (rightButtonStillDown && !e.ctrlKey && e.shiftKey) // don't place queued order when RMB is kept pressed (strafe orders are accepted)
      return;

    if (this.scene.minimap && this.scene.minimap.areCoordsOnMinimap({ x, y }) && !rightButtonStillDown) // clicked on the minimap
    {
      const { x: miniX, y: miniY } = this.scene.minimap.translateMinimapCoords({ x, y });
      x = miniX;
      y = miniY;
    }

    const elementsToCommand = this.getSelectedElements();
    let coordsPerElement: Iterator<CoordsType>;
    let target: Ship | undefined; // Initialize target variable
    let orderToGive: any; // eslint-disable-line
    const methodToUse = (e.shiftKey)
      ? 'queueOrder'
      : 'setOrder';

    /* get target if there is any */
    (() => {
      if (rightButtonStillDown) return;

      const matchedIds = this.getElementIdsByCoords(x, y);
      if (matchedIds.length > 0) // clicked on some elements
      {
        const selectedElementId = matchedIds.shift() as number;
        target = this.scene.elements[selectedElementId] as Ship;
        this.rightMouseButtonPressed = false; // selecting an element disable right click down to avoid moving near the target
      }
    })();

    if (this.currentLeftClickMode === LeftClickMode.ATTACK) {
      orderToGive = (target)
        ? (target.faction === Faction.PLAYER) // Attack-order on allied ships will actually order to follow them
          ? FollowOrder
          : AttackOrder
        : AttackMoveToOrder
    }
    else if (this.currentLeftClickMode === LeftClickMode.PATROL) {
      orderToGive = (target)
        ? FollowOrder
        : PatrolOrder
    }
    else {
      if (e.ctrlKey && !target)
        orderToGive = StrafeToOrder;
      else {
        orderToGive = (target && target.faction !== undefined)
          ? getFactionTargetGroups(Faction.PLAYER).includes(target.faction) // attack if enemy, follow if ally
            ? AttackOrder
            : FollowOrder
          : MoveToOrder // just move to coords
      }
    }

    /* keep rightclick down = always move */
    if (rightButtonStillDown) {
      orderToGive = e.ctrlKey
        ? StrafeToOrder
        : MoveToOrder
        ;
      target = undefined;
    }

    if (!target) {
      coordsPerElement = this.getCoordsForMultipleElements(
        { x, y },
        elementsToCommand,
      );
    }

    /* finally give orders to all the selected ships */
    elementsToCommand
      .forEach((element) => {
        const orderParam = (target)
          ? { target }
          : { x: coordsPerElement[element.id].x, y: coordsPerElement[element.id].y }
          ;
        if (element.behaviour) {
          element.behaviour[methodToUse](new orderToGive(orderParam));
        }
      });
  }

  handleMinimapClick(x: number, y: number): void {
    const scene = this.scene;
    if (!scene || !scene.minimap) return;
    
    const { x: cameraX, y: cameraY } = scene.minimap.translateMinimapCoords({ x, y });
    if (scene.camera) {
      scene.camera.goToCoords(cameraX, cameraY);
    }
    this.selection.onMinimap = true;
    this.selection.selecting = false;
  }

  onMouseMove = (e: MouseEvent): void => {
    const { x, y } = this.getPointerCoords(e);

    if (this.rightMouseButtonPressed && !e.shiftKey)
      return this.handleMovementOrder(x, y, e, true);

    if (this.scene.minimap && this.selection.onMinimap && this.scene.minimap.areCoordsOnMinimap({ x, y }))
      this.handleMinimapClick(x, y);
    else if (!this.selection.selecting)
      this.hoveredElementsIds = this.getElementIdsByCoords(x, y);
    else {
      this.selection.width = x - this.selection.x;
      this.selection.height = y - this.selection.y;
      this.hoveredElementsIds = this.getElementIdsBySelection();
    }
  };

  onMouseDown = (e: MouseEvent): void => {
    e.preventDefault();
    const button = ['left', 'middle', 'right'][e.button];
    const { x, y } = this.getPointerCoords(e);
    const camera = this.getCamera();

    switch (button) {
      case 'left':
        if (this.currentLeftClickMode !== LeftClickMode.SELECT) // Q-move or Patrol
        {
          this.handleMovementOrder(x, y, e);
          if (!e.shiftKey) {
            Game.instance.cursor.setDefaultSprite();
            this.currentLeftClickMode = LeftClickMode.SELECT;
          }
        }
        else if (this.scene.minimap && this.scene.minimap.areCoordsOnMinimap({ x, y }) && this.currentLeftClickMode === LeftClickMode.SELECT) // move camera by clicking on minimap
          this.handleMinimapClick(x, y);
        else if (this.currentLeftClickMode === LeftClickMode.SELECT) // Beginning of selection
        {
          this.selection.selecting = true;
          this.selection.x = x;
          this.selection.y = y;
          this.selection.width = this.selection.height = 0;
        }
        break;
      case 'right':
        if (this.currentLeftClickMode !== LeftClickMode.SELECT) {
          Game.instance.cursor.setDefaultSprite();
          this.currentLeftClickMode = LeftClickMode.SELECT; // cancel any left-click command
          return;
        }
        else {
          this.rightMouseButtonPressed = true;
          this.handleMovementOrder(x, y, e);
        }
        break;
      case 'middle':
        if (camera) {
          if (this.selectedElementsIds.length > 0)
            camera.targetElements = this.getSelectedElements();
        }
        break;
    }
  };

  onMouseUp = (e: MouseEvent): void => {
    e.preventDefault();
    const button = ['left', 'middle', 'right'][e.button];
    const camera = this.getCamera();

    switch (button) {
      case 'left':
        this.selectByMouseEvent(e);

        if (this.selection.onMinimap)
          this.selection.onMinimap = false;
        break;
      case 'right':
        this.rightMouseButtonPressed = false;
        break;
      case 'middle':
        if (camera)
          camera.targetElements = [];
        break;
    }
  };

  onMouseLeave = (e: MouseEvent): void => {
    this.onMouseUp(e);
  };

  onKeyDown = (e: KeyboardEvent): void => {
    if (
      e.code === 'Tab' // Tab is one of our shortcuts
      || (e.ctrlKey && ['KeyS', 'KeyD', 'KeyQ', 'KeyA'].includes(e.code)) // prevent browser shortcuts involving the ctrl key to mess with the user experience
    )
      e.preventDefault();

    const methodToUse = (e.shiftKey)
      ? 'queueOrder'
      : 'setOrder';

    const camera = this.getCamera();
    switch (e.code) {
      /* stop all selected elements */
      case 'KeyX':
        this.selectedElementsIds
          .map(id => this.scene.elements[id] as Ship)
          .forEach(ship => {
            if (ship && ship.behaviour) {
              ship.behaviour.clearOrders();
              ship.stop();
            }
          })
          ;
        break;

      /* Attack-move (or attack target) order */
      case 'KeyQ':
        if (e.ctrlKey)
          this.selectedElementsIds = this.selectAll();
        else {
          this.currentLeftClickMode = LeftClickMode.ATTACK;
          Game.instance.cursor.setCrossSprite();
        }
        break;
      /* Patrol-move order */
      case 'KeyP':
        this.currentLeftClickMode = LeftClickMode.PATROL;
        Game.instance.cursor.setCrossSprite();
        break;

      /* Hold position */
      case 'KeyH':
        this.selectedElementsIds
          .map(id => this.scene.elements[id] as Ship)
          .forEach(ship => {
            if (ship && ship.behaviour) {
              ship.stop();
              ship.behaviour.setOrder(new HoldPositionOrder());
            }
          })
        break;
      /* Return to CapitalShip to be stored */
      case 'KeyR':
        const capitalShip = Object.values(this.scene.elements).find((e: CapitalShip) => e.capital); // go to the CapitalShip
        if (this.selectedElementsIds.length === 1 && this.selectedElementsIds[0] === capitalShip.id) // capitalShip Selected
          capitalShip.handleRecall();
        else // other ships selected
        {
          this.selectedElementsIds
            .map(id => this.scene.elements[id] as Ship | CapitalShip)
            .filter(e => e?.parentShip)
            .forEach(ship => {
              if (ship && ship.behaviour) {
                ship.stop();
                ship.behaviour[methodToUse](new FollowOrder({
                  target: ship.parentShip as Ship,
                  distanceToReach: ship.width,
                  onDistanceReached: (modifier: number, self: FollowOrder) => {
                    if (!ship.parentShip?.isInScene) return;
                    ship.parentShip.storeShip(self.getElement())
                  }
                }));
              }
            })
        }
        break;

      /* Switch to ManualControls */
      case 'KeyC':
        this.disable();
        this.hoveredElementsIds = [];
        Game.instance.controlSchemes.manual?.enable();
        if (camera) {
          camera.targetElements = this.getSelectedElements();
        }
        break;

      /* Toggle weapons range display */
      case 'KeyV':
        if (this.hoveredElementsIds.length > 0) {
          this.scene.getElementsByIds(this.hoveredElementsIds).forEach((e) => {
            const ship = e as Ship;
            ship?.weapons?.forEach((w) => {
              w.isRangeDrawn = !w?.isRangeDrawn;
            })
          });
        } else {
          this.scene.getElementsByIds(this.selectedElementsIds).forEach((e) => {
            const ship = e as Ship;
            ship?.weapons?.forEach((w) => {
              w.isRangeDrawn = !w?.isRangeDrawn;
            })
          })
        }
        break;

      case 'ArrowUp':
      case 'KeyW':
        if (camera)
          camera.movement.y = -1;
        e.preventDefault();
        break;
      case 'ArrowDown':
      case 'KeyS':
        if (camera)
          camera.movement.y = 1;
        e.preventDefault();
        break;
      case 'ArrowLeft':
      case 'KeyA':
        if (camera)
          camera.movement.x = -1;
        e.preventDefault();
        break;
      case 'ArrowRight':
      case 'KeyD':
        if (camera)
          camera.movement.x = 1;
        e.preventDefault();
        break;
      case 'Space':
        Game.instance.togglePause();
        break;

      case 'Tab':
        if (Object.values(Menu.openMenus).length > 0)
          Menu.closeAll();
        else
          Object.values(this.scene.elements).filter((e: CapitalShip) => e.capital)[0]?.openMenu();
        break;
      case 'ControlLeft':
        this.ctrlKeyPressed = true;
        break;
      case 'KeyJ':
        this.scene.getElementsByIds(this.selectedElementsIds)
          .filter((element): element is Ship => element instanceof Ship)
          .forEach((ship) => {
            ship.toggleJump(); // Jump!
          })
        break;
    }

    /* Control groups management */
    const groupNumber = e.code.substring('Digit'.length);
    if (Object.keys(this.controlGroups).includes(groupNumber)) // [0123456789] pressed
    {
      e.preventDefault();
      const controlGroup = this.controlGroups[parseInt(groupNumber)];
      if (e.ctrlKey) // set elements in a group
        controlGroup.setElementsIds(this.selectedElementsIds);
      else if (e.shiftKey) // add element(s) to group
        controlGroup.addElementsIds(this.selectedElementsIds);
      else // select the elements from a group
      {
        if (controlGroup.elementsIds?.length > 0) // group has "members"
        {
          this.currentLeftClickMode = LeftClickMode.SELECT;
          const now = Date.now();
          this.selectedElementsIds = [...controlGroup.elementsIds];
          if (now - controlGroup.lastSelectTimestamp < controlGroup.doubleSelectTimeout)
            controlGroup.focusOnMyElements();
          controlGroup.lastSelectTimestamp = now;
        }
      }
    }
  };

  onKeyUp = (e: KeyboardEvent): void => {
    const camera = this.getCamera();
    switch (e.code) {
      case 'ArrowUp':
      case 'KeyW':
        if (camera && camera.movement.y === -1)
          camera.movement.y = 0;
        break;
      case 'ArrowDown':
      case 'KeyS':
        if (camera && camera.movement.y === 1)
          camera.movement.y = 0;
        break;
      case 'ArrowLeft':
      case 'KeyA':
        if (camera && camera.movement.x === -1)
          camera.movement.x = 0;
        break;
      case 'ArrowRight':
      case 'KeyD':
        if (camera && camera.movement.x === 1)
          camera.movement.x = 0;
        break;
      case 'ControlLeft':
        this.ctrlKeyPressed = false;
        break;
      case 'ShiftLeft': // reset leftClickmode 
        this.currentLeftClickMode = LeftClickMode.SELECT;
        Game.instance.cursor.setDefaultSprite();
        break;
    }
  };

  onMouseWheel = (e: WheelEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    const { deltaY } = e;
    const camera = this.getCamera();
    if (camera) {
      const zoomRatioPerScroll = 1.1;
      const zoomToApply = (deltaY < 0)
        ? zoomRatioPerScroll
        : 1 / zoomRatioPerScroll
        ;
      camera.zoom(zoomToApply); // Zoom in
    }
  };

  enable(): RTSControls {
    super.enable();
    return this;
  }

  disable(): RTSControls {
    super.disable();
    this.rightMouseButtonPressed = false;
    this.selection.selecting = false;
    return this;
  }

  selectByMouseEvent(e: MouseEvent): void {
    if (e.button && e.button !== 0) // not left click
      return;

    const { x, y } = this.getPointerCoords(e);
    const isSimpleClick = (
      isBetween(this.selection.width, 0, 1)
      && isBetween(this.selection.height, 0, 1)
    );
    let selectedElementsIds = (isSimpleClick)
      ? this.getElementIdsByCoords(x, y)
      : this.getElementIdsBySelection()
      ;

    /* Only player-controlled units are selectable */
    if (this.currentLeftClickMode === LeftClickMode.SELECT) {
      selectedElementsIds = selectedElementsIds
        .filter(id => this.scene.elements[id].faction === Faction.PLAYER)
        ;
    }

    this.selection.selecting = false;

    /* simple click on multiple stacked elements */
    const singleSelectionDeadMargin = 2;
    if (
      Math.abs(this.selection.width) < singleSelectionDeadMargin
      && Math.abs(this.selection.height) < singleSelectionDeadMargin
      && selectedElementsIds.length > 0
    ) {
      selectedElementsIds = [selectedElementsIds[0]];
    }

    /* Pressing the shift key while will add/remove the selected element to the already existing selection */
    if (e.shiftKey) {
      if (selectedElementsIds.length === 1 && this.selectedElementsIds.includes(selectedElementsIds[0]))
        selectedElementsIds = this.selectedElementsIds.filter(e => e !== selectedElementsIds[0]);
      else
        selectedElementsIds = [...this.selectedElementsIds, ...selectedElementsIds];
    }

    if (selectedElementsIds.length === 1) {
      const now = Date.now();
      if (now - this.lastSelectTimestamp < this.doubleSelectTimeout) {
        const element = this.scene.elements[selectedElementsIds[0]];
        if (element) {
          selectedElementsIds = this.selectAllIdenticalUnits(element);
        }
      }
      this.lastSelectTimestamp = now;
    }

    if (selectedElementsIds.length > 0)
      this.selectedElementsIds = selectedElementsIds;
  }

  getSelectableElementIds(box: { x: number, y: number, width: number, height: number }): Array<IdentifierType> {
    const { x, y, width, height } = box;
    return this.scene.quadtree
      .filter((item: QuadtreeItem) => this.scene.elements[item.id]?.selectable ?? false)
      .filter((item: QuadtreeItem) => !this.scene.elements[item.id]?.isInWarFog)
      ?.colliding({ x, y, width, height })
      ?.map((e: QuadtreeItem) => e.id)
  }

  getElementIdsBySelection(): Array<IdentifierType> {
    let { x, y, width, height } = this.selection;

    /* width and height must be > 0 */
    if (width < 0) {
      x += width;
      width = Math.abs(width);
    }
    if (height < 0) {
      y += height;
      height = Math.abs(height);
    }

    return this.getSelectableElementIds({ x, y, width, height });
  }

  getElementIdsByCoords(x: number, y: number): Array<IdentifierType> {
    const margin = 10;
    return this.getSelectableElementIds({
      x: x - margin,
      y: y - margin,
      width: margin * 2,
      height: margin * 2,
    });
  }

  get allPlayerElements(): Element[] {
    return Object.values(this.scene.elements)
      .filter(el => el.faction === Faction.PLAYER)
      ;
  }

  getSelectedElements(): Array<Element> {
    return this?.scene.getElementsByIds(this.selectedElementsIds);
  }

  selectAll(): IdentifierType[] {
    return this.allPlayerElements
      .map(el => el.id)
      ;
  }

  selectAllIdenticalUnits(selectedUnit: Element): IdentifierType[] {
    return this.allPlayerElements
      .filter(el => el.isInCamera)
      .filter(el => el.width === selectedUnit.width && el.height === selectedUnit.height && el.sprite?.src === selectedUnit.sprite?.src)
      .map(el => el.id)
      ;
  }

  drawSelector(): CanvasRenderingContext2D | false {
    if (!this.selection.selecting)
      return false;
    const { x, y, width, height } = this.selection;
    this.context.strokeStyle = RTSControls.colors.selector;
    this.context.lineWidth = RTSControls.selectionWidth;
    this.context.strokeRect(x, y, width, height);
    return this.context;
  }

  undrawSelector(): CanvasRenderingContext2D | false {
    const { x, y, width, height } = this.selection;
    const centerX = x + width / 2,
      centerY = y + height / 2
      ;
    this.context.clearRect(
      centerX - width,
      centerY - height,
      width * 2,
      height * 2,
    );
    return this.context;
  }

  getElementSelectionColor(element: Element): string {
    switch (element.faction) {
      case Faction.PLAYER:
        return RTSControls.colors.selector;
      case Faction.HAZARD:
        return RTSControls.colors.hoverHazard;
      case Faction.ENEMY:
        return RTSControls.colors.hoverEnemy;
      default:
        return RTSControls.colors.hoverNeutral;
    }
  }

  getElementHoverColor(element: Element): string {
    let color = this.getElementSelectionColor(element);
    if (element.faction === Faction.PLAYER)
      color = RTSControls.colors.hover;
    return color;
  }

  drawElementSelection(element: Element, color?: string): CanvasRenderingContext2D {
    const { x = 0, y = 0, width = 0 } = element;
    const radius = width / 2 + 10;
    const { context } = this;

    if (!color)
      color = this.getElementSelectionColor(element);

    context.strokeStyle = color;
    context.lineWidth = RTSControls.selectionWidth;

    context.moveTo(x, y);
    context.beginPath();
    context.arc(x, y, radius, 0, 2 * Math.PI);
    context.stroke();
    return context;
  }

  drawElementHover(element: Element, color?: string): CanvasRenderingContext2D {
    const { context } = this;
    context.save();
    context.setLineDash([10]);

    if (!color)
      color = this.getElementHoverColor(element);

    this.drawElementSelection(element, color);

    context.restore();
    return context;
  }

  undrawElementSelection(element: Element): CanvasRenderingContext2D {
    const { x = 0, y = 0, width = 0, height = 0 } = element;
    const radius = Math.max(width, height) + RTSControls.selectionWidth + 1;

    const { context: ctx } = this;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.moveTo(x, y);
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.fill();
    ctx.restore();
    return ctx;
  }
}
